import { useState, useEffect, useRef, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectUploads, setUploads, renameUpload } from "./UploadsSlice";
import appConfig from "../../appConfig";
import { useMsal } from "@azure/msal-react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import { Link } from "react-router-dom";
import { fetchSecure, getToken } from "../../services/apiService";
import { environmentLabel } from "./template";
import useFinishedNotifications, { askNotificationPermission } from "./useFinishedNotifications";
import {
  canOpenComparison,
  isBomOutput,
  isComparisonOutput,
  isExternalOutput,
  isTraceOutput,
  shortEnvironment,
  summaryOutputFor,
} from "./outputs";
import FieldHelp from "../help/FieldHelp";
import "./FileUpload.css";
import "./UploadList.css";

const REFRESH_INTERVAL_MS = 10000;
const SHOW_COMPILED_PDF = false;

const bomViewerUrl = (output) => `/bom/${output.id}`;

// Menu of a row action. Fixed so the scrolling table doesn't cut it; the style makes it fixed before Popper first
// measures it, otherwise Popper measures it against the button and it opens in the corner of the screen
const RowMenu = ({ label, variant, title, children }) => (
  <Dropdown>
    <Dropdown.Toggle size="sm" variant={variant} title={title}>
      {label}
    </Dropdown.Toggle>
    <Dropdown.Menu popperConfig={{ strategy: "fixed" }} style={{ position: "fixed" }}>
      {children}
    </Dropdown.Menu>
  </Dropdown>
);

// Opens the viewer in a new tab: one button, or a menu when the input has several BOMs (Dev and Prd, Agile and Oracle...)
const ViewBomButton = ({ outputs }) => {
  if (outputs.length === 0) return null;
  if (outputs.length === 1)
    return (
      <a className="btn btn-sm btn-outline-primary" href={bomViewerUrl(outputs[0])} target="_blank" rel="noopener noreferrer" title="Open the BOM in a new tab">
        View BOM
      </a>
    );
  return (
    <RowMenu label="View BOM" variant="outline-primary" title="Open a BOM in a new tab">
      {outputs.map((output) => (
        <Dropdown.Item key={output.id} href={bomViewerUrl(output)} target="_blank" rel="noopener noreferrer">
          {environmentLabel(output.environment)}
        </Dropdown.Item>
      ))}
    </RowMenu>
  );
};

// Comparison Excel files. Infor vs Infor makes one per environment, in a menu; when there is no web comparison
// (Agile / Oracle) it is the main result and keeps the solid button
const ExcelButton = ({ comparisons, primary, busy, onDownload }) => {
  if (comparisons.length === 0) return null;
  const variant = primary ? "success" : "outline-success";
  if (comparisons.length === 1) {
    const [output] = comparisons;
    return (
      <button
        type="button"
        className={`btn btn-sm btn-${variant}`}
        title={`Download ${output.name}`}
        disabled={busy(output)}
        onClick={() => onDownload(output)}
      >
        {primary ? "Comparison" : "Excel"}
      </button>
    );
  }
  return (
    <RowMenu label={primary ? "Comparison" : "Excel"} variant={variant} title="Download the comparison files">
      {comparisons.map((output) => (
        <Dropdown.Item key={output.id} as="button" type="button" disabled={busy(output)} onClick={() => onDownload(output)}>
          {shortEnvironment(output.environment)} comparison
          <div className="small text-muted">{output.name}</div>
        </Dropdown.Item>
      ))}
    </RowMenu>
  );
};

// The comparison viewer of an input, optionally on one tab, with one kind of row, or only NOT FOUND / ERROR
const compareUrl = (input, { tab, show, issues } = {}) => {
  const params = new URLSearchParams();
  if (tab) params.set("tab", tab);
  if (show) params.set("show", show);
  if (issues) params.set("issues", "1");
  const query = params.toString();
  return `/compare/${input.id}${query ? `?${query}` : ""}`;
};

const CompareButton = ({ input }) =>
  canOpenComparison(input) ? (
    <a className="btn btn-sm btn-primary" href={compareUrl(input)} target="_blank" rel="noopener noreferrer" title="See the differences in a new tab">
      Compare
    </a>
  ) : null;

// Summary line of one tab -> { tab, parts }: "GenParts: 121 added in Dev, 121 deleted in Dev"
const parseSummaryLine = (line) => {
  const match = /^(.+?): (.+)$/.exec(line);
  return match ? { tab: match[1], parts: match[2].split(", ").map((part) => part.replace(/^\d+ /, "")) } : null;
};

// Row status of a Result chip ("changed", "added in Dev", "deleted in Dev")
const chipStatus = (label) => ["changed", "added", "deleted"].find((status) => label.startsWith(status));

// Where a Result chip leads: the comparison on the first tab with that kind of row, showing only those rows;
// NOT FOUND / ERROR opens the rows with them, in the comparison or in the BOM that has them
const chipUrl = (input, summary, item) => {
  const boms = input.outputs.filter((o) => isBomOutput(o) && !isExternalOutput(o)).sort((a, b) => a.id - b.id);
  if (item.tone === "critical") {
    if (canOpenComparison(input)) return compareUrl(input, { issues: true });
    const bom = boms.find((o) => item.label.endsWith(` in ${shortEnvironment(o.environment)}`)) || (boms.length === 1 ? boms[0] : null);
    return bom ? `${bomViewerUrl(bom)}?issues=1` : null;
  }
  const status = chipStatus(item.label);
  if (!status || !canOpenComparison(input)) return null;
  const tab = summary.details.map(parseSummaryLine).find((line) => line?.parts.some((part) => part.startsWith(status)))?.tab;
  return compareUrl(input, { tab, show: status });
};

const getOutputKind = (output) => {
  if (isTraceOutput(output)) return { label: "Trace", badge: "text-bg-info" };
  if (isComparisonOutput(output)) return { label: "Comparison", badge: "text-bg-success" };
  return { label: "Raw BOM", badge: "text-bg-secondary" };
};

const getRunLabel = (input) => {
  if (input.environmentA) {
    const first = environmentLabel(input.environmentA);
    const section = input.sectionToProcess ? ` (section ${input.sectionToProcess})` : "";
    if (input.compareInputId) return `${first}${section} vs previous run #${input.compareInputId}`;
    const oracle = [input.esoi && `ESOI ${input.esoi}`, input.agileRevision && `Agile rev ${input.agileRevision}`, input.plant, input.workOrder && `WO ${input.workOrder}`].filter(Boolean).join(", ");
    const details = oracle ? ` (${oracle})` : "";
    return input.environmentB
      ? `${first}${section} vs ${environmentLabel(input.environmentB)}${details}`
      : `${first}${section}${details} only`;
  }
  // Inputs created before environments could be chosen
  if ((input.generateOnlyDevBom || "").trim().toLowerCase() === "yes") return "On-Premise Dev only";
  return "On-Premise Dev vs On-Premise Prd";
};

// Progress of a download next to its button: "Downloading...", "Complete", "Failed" or "Error"
const DOWNLOAD_TONES = { Complete: "text-success", Failed: "text-danger", Error: "text-danger" };
const DownloadStatus = ({ text }) =>
  text ? (
    <span className={`ms-2 small ${DOWNLOAD_TONES[text] || "text-muted"}`} role="status">
      {text}
    </span>
  ) : null;

const isUploadActive = (upload) => upload && (upload.inQueue > 0 || upload.inProcess > 0);

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SHOW_TEST_SET_FILTER = false;

const MANUAL_UPLOAD_PATTERN = /manual-input-\d+\.xlsx$/i;
const RERUN_PREFIX = "Rerun of ";

const getUploadDisplay = (upload) => {
  const name = upload.name || "";
  const isRerun = name.startsWith(RERUN_PREFIX);
  const baseName = isRerun ? name.slice(RERUN_PREFIX.length) : name;
  const isManual = MANUAL_UPLOAD_PATTERN.test(baseName);
  // Test sets made before names existed show their file name
  const text = upload.title || (isManual ? `Manual input #${upload.id}` : baseName);
  if (isRerun) return { source: "rerun", sourceLabel: "Rerun", text };
  return isManual ? { source: "manual", sourceLabel: "Manual", text } : { source: "template", sourceLabel: "Template", text };
};

const uploadName = (upload) => getUploadDisplay(upload).text;

const FINISHED_STATUSES = ["Done", "Failed"];

const getUploadStatus = (upload) => {
  if (!upload.total) return { label: "Empty", tone: "muted" };
  if (upload.inProcess > 0) return { label: "Running", tone: "running" };
  if (upload.inQueue > 0) return { label: "Queued", tone: "queued" };
  if (upload.failed === upload.total) return { label: "Failed", tone: "failed" };
  if (upload.failed > 0) return { label: "Done with errors", tone: "warning" };
  return { label: "Done", tone: "done" };
};

// The API sends "yyyy-MM-dd HH:mm", already in plant time
const formatCreated = (created) => {
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(created || "");
  if (!match) return created;
  const [, year, month, day, hour, minute] = match.map(Number);
  const date = new Date(year, month - 1, day);
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}, ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const PROGRESS_SEGMENTS = [
  { key: "done", label: "done", className: "bg-success" },
  { key: "failed", label: "failed", className: "bg-danger" },
  { key: "inProcess", label: "running", className: "bg-primary progress-bar-striped progress-bar-animated" },
  { key: "inQueue", label: "queued", className: "bg-secondary opacity-50" },
];

const UploadProgress = ({ upload }) => {
  const total = upload.total || 0;
  const parts = PROGRESS_SEGMENTS.filter((s) => upload[s.key] > 0);
  return (
    <div>
      <div className="progress uploads-progress" role="progressbar" aria-label="Test set progress">
        {total > 0 &&
          parts.map((s) => (
            <div key={s.key} className={`progress-bar ${s.className}`} style={{ width: `${(upload[s.key] / total) * 100}%` }} />
          ))}
      </div>
      <div className="uploads-progress__legend">
        {total === 0
          ? "No inputs"
          : [`${upload.done || 0} of ${total} done`, ...parts.filter((s) => s.key !== "done").map((s) => `${upload[s.key]} ${s.label}`)].join(" · ")}
      </div>
    </div>
  );
};

const MAX_SUMMARY_CHIPS = 3;

// A chip that opens its rows in a new tab when linkFor gives a URL
const SummaryChip = ({ item, url }) => {
  const content = (
    <>
      <b>{item.count.toLocaleString()}</b> {item.label}
    </>
  );
  return url ? (
    <a className={`summary-chip summary-chip--${item.tone} summary-chip--link`} href={url} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <span className={`summary-chip summary-chip--${item.tone}`}>{content}</span>
  );
};

// Counts from the comparison's Summary sheet: { loading } | { data } | { error }; data null = nothing to show.
// linkFor(item) -> URL of the rows behind a chip, or null
const ComparisonSummary = ({ summary, linkFor = () => null }) => {
  if (!summary) return <span className="text-muted">-</span>;
  if (summary.loading) return <span className="text-muted small">Reading...</span>;
  if (summary.error || !summary.data) return <span className="text-muted">-</span>;

  const { items, details, compared } = summary.data;
  const tooltip = details.join("\n");
  // Part Number NOT FOUND / ERROR always shows, before the comparison counts
  const critical = items.filter((item) => item.tone === "critical");
  const rest = items.filter((item) => item.tone !== "critical");
  const noDifferences = compared !== false && rest.length === 0;
  if (items.length === 0) {
    return (
      <span className="summary-chip summary-chip--success" title={tooltip}>
        No differences
      </span>
    );
  }
  const shown = [...critical, ...rest.slice(0, MAX_SUMMARY_CHIPS)];
  const hidden = rest.length - Math.min(rest.length, MAX_SUMMARY_CHIPS);
  return (
    <div className="summary-chips" title={tooltip}>
      {shown.map((item) => (
        <SummaryChip key={item.label} item={item} url={linkFor(item)} />
      ))}
      {noDifferences && <span className="summary-chip summary-chip--success">No differences</span>}
      {hidden > 0 && <span className="summary-chip summary-chip--more">+{hidden} more</span>}
    </div>
  );
};

const SUMMARY_FETCH_LIMIT = 4;

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

// Name of the test set, edited in place: Enter saves, Esc cancels. onSave returns an error text or null
const UploadName = ({ upload, display, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const start = () => {
    setValue(display.text);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const title = value.trim();
    if (!title) {
      setError("Type a name.");
      return;
    }
    if (title === display.text) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const problem = await onSave(title);
    setSaving(false);
    if (problem) setError(problem);
    else setEditing(false);
  };

  if (editing) {
    return (
      <div className="uploads-name uploads-name--editing" onClick={(e) => e.stopPropagation()}>
        <div className="flex-grow-1">
          <input
            type="text"
            className={`form-control form-control-sm${error ? " is-invalid" : ""}`}
            aria-label="Test set name"
            maxLength={128}
            autoFocus
            disabled={saving}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              } else if (e.key === "Escape") {
                setEditing(false);
              }
            }}
          />
          {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
        <button type="button" className="btn btn-sm btn-primary" disabled={saving} onClick={save}>
          {saving ? "Saving..." : "Save"}
        </button>
        <button type="button" className="btn btn-sm btn-link" disabled={saving} onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="uploads-name">
      <span className={`uploads-source uploads-source--${display.source}`}>{display.sourceLabel}</span>
      <span className="uploads-name__text" title={upload.name}>
        {display.text}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-link uploads-name__edit"
        aria-label={`Rename ${display.text}`}
        title="Rename"
        onClick={(e) => {
          e.stopPropagation();
          start();
        }}
      >
        <PencilIcon />
      </button>
    </div>
  );
};

export default function UploadList() {
  const { instance, accounts } = useMsal();
  const uploads = useSelector(selectUploads);
  const dispatch = useDispatch();
  const [inputs, setInputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedName, setSelectedName] = useState("");
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const latestUploadsRequest = useRef(0);
  const initialLoadDone = useRef(false);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [expandedInputs, setExpandedInputs] = useState({});
  const [downloadingIds, setDownloadingIds] = useState({});
  const [downloadStatus, setDownloadStatus] = useState({});
  const [downloadStatusDrawing, setDownloadStatusDrawing] = useState({});
  
  const [currentDownloadData, setCurrentDownloadData] = useState({ id: null, name: "" });
  const [comments, setComments] = useState("");
  const [showModalInputs, setShowModalInputs] = useState(false);

  // Multi-select of drawings to export as a single compiled PDF.
  const [selectedDrawingIds, setSelectedDrawingIds] = useState([]);
  const [compiledMode, setCompiledMode] = useState(false);
  const [downloadingCompiled, setDownloadingCompiled] = useState(false);
  const [compiledStatus, setCompiledStatus] = useState("");
  const [rerunning, setRerunning] = useState({});
  const [notice, setNotice] = useState(null);
  // Rerun waiting for confirmation: { uploadId, inputIds, label }
  const [confirmRerun, setConfirmRerun] = useState(null);

  const toggleDrawingSelection = (outputId) => {
    setSelectedDrawingIds((prev) =>
      prev.includes(outputId) ? prev.filter((id) => id !== outputId) : [...prev, outputId]
    );
  };

  const clearDrawingSelection = () => setSelectedDrawingIds([]);

  const handleCloseInputs = () => {
    setShowModalInputs(false);
  };

  const fetchUploads = async () => {
    await getUploads();
  };

  // Comparison summaries by output id. Files never change, so each one is read once per page load
  const [summaries, setSummaries] = useState({});
  const requestedSummaries = useRef(new Set());

  useEffect(() => {
    if (!showModalInputs) return;
    const pending = inputs
      .map(summaryOutputFor)
      .filter((output) => output && !requestedSummaries.current.has(output.id));
    if (pending.length === 0) return;
    pending.forEach((output) => requestedSummaries.current.add(output.id));
    // A retry keeps showing "-" instead of flashing "Reading..."
    setSummaries((prev) => ({ ...Object.fromEntries(pending.map((o) => [o.id, { loading: true }])), ...prev }));

    const loadSummaries = async () => {
      const accessToken = await getToken(instance, accounts);
      const queue = [...pending];
      const worker = async () => {
        while (queue.length > 0) {
          const output = queue.shift();
          const data = accessToken ? await fetchSecure(accessToken, `${appConfig.apiUrl}outputs/${output.id}/summary`) : null;
          const ok = data && data.status === "OK";
          // A failed read can be tried again the next time the inputs load
          if (!ok) requestedSummaries.current.delete(output.id);
          setSummaries((prev) => ({ ...prev, [output.id]: ok ? { data: data.data } : { error: true } }));
        }
      };
      await Promise.all(Array.from({ length: Math.min(SUMMARY_FETCH_LIMIT, queue.length) }, worker));
    };
    loadSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, showModalInputs]);

  const fetchInputs = async (uploadId, name, { silent = false } = {}) => {
    const accesToken = await getToken(instance, accounts);
    if (!accesToken) {
      setError("Authentication error. Please sign in again.");
      return;
    }

    const url = `${appConfig.apiUrl}inputs/${uploadId}`;
    const data = await fetchSecure(accesToken, url);
    if (!data || data.status !== "OK") {
      if (!silent) setError("Error fetching inputs.");
      return;
    }

    setInputs(data.data);
    if (!silent) {
      setSelectedName(name);
      setSelectedUploadId(uploadId);
      setShowModalInputs(true);
    }
  };

  const getUploads = async ({ silent = false } = {}) => {
    const requestId = ++latestUploadsRequest.current;
    const isStale = () => requestId !== latestUploadsRequest.current;
    try {
      if (!silent) setLoading(true);
      const accesToken = await getToken(instance, accounts);
      if (!accesToken) {
        setError("Authentication error. Please sign in again.");
        setLoading(false);
        return;
      }

      const term = filter.trim();
      const filterParam = term ? `&search=${encodeURIComponent(term)}` : "";
      const url = `${appConfig.apiUrl}uploads?pageNumber=${pageNumber}&pageSize=${pageSize}${filterParam}`;
      const data = await fetchSecure(accesToken, url);
      if (isStale()) return;
      if (!data || data.status !== "OK" || !data.data) {
        setError(data?.message || "Error fetching test sets.");
        setLoading(false);
        return;
      }

      const result = data.data;
      dispatch(setUploads(result.items));
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
      setLoading(false);
      setError(null);
    } catch (err) {
      if (isStale()) return;
      setError("Error fetching test sets.");
      setLoading(false);
    }
  };

  const createUser = async (email) => {
    const accesToken = await getToken(instance, accounts);
    if (!accesToken) {
      setError("Authentication error. Please sign in again.");
      return null;
    }

    const url = `${appConfig.apiUrl}users`;
    const data = await fetchSecure(accesToken, url, {
      method: "POST",
      data: { email },
    });
    return data;
  }

  const checkUser = async () => {
    const accesToken = await getToken(instance, accounts);
    if (!accesToken) {
      setError("Authentication error. Please sign in again.");
      setLoading(false);
      return;
    }

    const url = `${appConfig.apiUrl}users`;
    const response = await fetchSecure(accesToken, url);
    if (!response) {
      setError("Error validating user session.");
      setLoading(false);
      return;
    }

    if (response.status === 'OK') {
      if (response.data.id === 0) {
        await createUser(response.data.email);
      }
      await fetchUploads(pageNumber);
    } else if (response.status === "Error") {
      setError(response.message);
      setLoading(false);
    }
  };

  const logUser = async () => {
    const accessToken = await getToken(instance, accounts);
    if (!accessToken) {
      return;
    }

    const url = `${appConfig.apiUrl}users/log?appName=BOMCompare-web-visit`;
    await fetchSecure(accessToken, url);
  };

  useEffect(() => {
    const hasRun = sessionStorage.getItem('hasRunFunction');
    if (!hasRun) {
      logUser();
      sessionStorage.setItem('hasRunFunction', 'true');
    }
    checkUser();
  }, []);

  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      return;
    }
    getUploads({ silent: true });
  }, [pageNumber, pageSize, filter]);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setPageNumber(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPageNumber(1);
  };

  // Keep statuses (and the open inputs dialog) up to date while something is queued or running
  const hasActiveUploads = uploads.some(isUploadActive);
  useEffect(() => {
    if (!hasActiveUploads) return undefined;
    const timer = setInterval(() => {
      getUploads({ silent: true });
      if (showModalInputs && selectedUploadId) {
        fetchInputs(selectedUploadId, selectedName, { silent: true });
      }
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
    // Loader functions are recreated every render; the interval only needs to restart when these change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveUploads, pageNumber, pageSize, filter, showModalInputs, selectedUploadId]);

  const finishedNotices = useFinishedNotifications(uploads, {
    isActive: isUploadActive,
    nameOf: uploadName,
    onOpen: (item) => {
      finishedNotices.dismiss(item.id);
      fetchInputs(item.id, item.name);
    },
  });

  if (loading && uploads.length === 0) {
    return <div>Loading test sets...</div>;
  }

  const handleNextPage = async () => {
    if (pageNumber < totalPages) {
      setPageNumber((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = async () => {
    if (pageNumber > 1) {
      setPageNumber((prevPage) => prevPage - 1);
    }
  };

  const downloadOutputFile = async (outputId, fileName) => {
    const key = `file-${outputId}`;
    setDownloadStatus((prev) => ({ ...prev, [key]: "Downloading..." }));
    try {
      const accesToken = await getToken(instance, accounts);
      const url = `${appConfig.apiUrl}outputs/download/${outputId}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accesToken}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        setDownloadStatus((prev) => ({ ...prev, [key]: undefined }));
      } else {
        setDownloadStatus((prev) => ({ ...prev, [key]: "Failed" }));
      }
    } catch (err) {
      setDownloadStatus((prev) => ({ ...prev, [key]: "Error" }));
    }
  };

  const downloadAllFiles = async (uploadId, name) => {
    const key = `set-${uploadId}`;
    setDownloadStatus((prev) => ({ ...prev, [key]: "Downloading..." }));
    try {
      const accesToken = await getToken(instance, accounts);
      const response = await fetch(`${appConfig.apiUrl}uploads/${uploadId}/files`, {
        headers: { Authorization: `Bearer ${accesToken}` },
      });
      if (!response.ok) {
        setDownloadStatus((prev) => ({ ...prev, [key]: "Failed" }));
        return;
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${(name || `Test set ${uploadId}`).replace(/[\\/:*?"<>|]/g, "_")}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadStatus((prev) => ({ ...prev, [key]: undefined }));
    } catch (err) {
      setDownloadStatus((prev) => ({ ...prev, [key]: "Error" }));
    }
  };

  const downloadOutputFolders = async (outputId, fileName) => {
    setDownloadingIds((prev) => ({ ...prev, [outputId]: true }));
    setDownloadStatus((prev) => ({ ...prev, [outputId]: "Downloading..." }));

    try {
      const accesToken = await getToken(instance, accounts);
      const url = `${appConfig.apiUrl}outputs/startDownloadOutputFolders/${outputId}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accesToken}` },
      });

      if (response.ok) {
        const jobId = await response.json();

        while (true) {
          const url = `${appConfig.apiUrl}outputs/downloadOutputFolders/${jobId}`;
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${accesToken}` },
          });

          if (response.status === 200) {
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            setDownloadStatus((prev) => ({ ...prev, [outputId]: "Complete" }));
            break;
          } else if (response.status === 209) {
            await new Promise((r) => setTimeout(r, 5000));
          } else {
            throw new Error(`Unexpected status: ${response.status}`);
          }
        }
      } else {
        setDownloadStatus((prev) => ({ ...prev, [outputId]: "Failed" }));
      }
    } catch (err) {
      setDownloadStatus((prev) => ({ ...prev, [outputId]: "Error" }));
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [outputId]: false }));
    }
  };

 const downloadAEDrawing = async (outputId, fileName, longText) => {
  setDownloadingIds((prev) => ({ ...prev, [outputId]: true }));
  setDownloadStatusDrawing((prev) => ({ ...prev, [outputId]: "Downloading..." }));

  try {
    const accesToken = await getToken(instance, accounts);
  
    const url = `${appConfig.apiUrl}outputs/startDownloadAEDrawing`; 
    
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accesToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        OutputId: outputId,
        Comment: longText
      }),
    });

    if (response.ok) {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadStatusDrawing((prev) => ({ ...prev, [outputId]: "Complete" }));
    } else {
      setDownloadStatusDrawing((prev) => ({ ...prev, [outputId]: "Failed" }));
    }
  } catch (err) {
    setDownloadStatusDrawing((prev) => ({ ...prev, [outputId]: "Error" }));
  } finally {
    setDownloadingIds((prev) => ({ ...prev, [outputId]: false }));
  }
};

const setCompiledFileName = () => {
  const formattedDate = new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).replaceAll("/", "-");

  return `Compiled_Drawings-${formattedDate}.pdf`;
};

const downloadCompiledAEDrawings = async (outputIds, longText) => {
  if (!outputIds || outputIds.length === 0) return;

  setDownloadingCompiled(true);
  setCompiledStatus("Compiling...");

  try {
    const accesToken = await getToken(instance, accounts);

    const url = `${appConfig.apiUrl}outputs/startDownloadCompiledAEDrawing`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accesToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        OutputIds: outputIds,
        Comment: longText,
      }),
    });

    if (response.ok) {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = setCompiledFileName();
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setCompiledStatus("Complete");
      clearDrawingSelection();
    } else {
      setCompiledStatus("Failed");
    }
  } catch (err) {
    setCompiledStatus("Error");
  } finally {
    setDownloadingCompiled(false);
  }
};

  const setFolderZipName = (outputName) => {
    const formattedDate = new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).replaceAll("/", "-");

    return `BOMParts_${outputName}-${formattedDate}.zip`;
  };

  const setAEDrawingFileName = (outputName) => {
    const formattedDate = new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).replaceAll("/", "-");

    return `Drawing_${outputName}-${formattedDate}.pdf`;
  };

  // Returns the error text for the name editor, or null when saved
  const saveUploadName = async (uploadId, title) => {
    const accessToken = await getToken(instance, accounts);
    if (!accessToken) return "Authentication error. Please sign in again.";
    const data = await fetchSecure(accessToken, `${appConfig.apiUrl}uploads/${uploadId}`, {
      method: "PATCH",
      data: { title },
    });
    if (!data || data.status !== "OK") return data?.message || "The name could not be saved.";
    dispatch(renameUpload({ id: uploadId, title: data.data.title }));
    return null;
  };

  // Reruns create a new test set with copies of the inputs; the original results are kept. Asked first (confirmRerun).
  const rerunUpload = async (uploadId, inputIds) => {
    const key = inputIds ? `input-${inputIds[0]}` : `upload-${uploadId}`;
    setRerunning((prev) => ({ ...prev, [key]: true }));
    try {
      const accessToken = await getToken(instance, accounts);
      if (!accessToken) {
        setError("Authentication error. Please sign in again.");
        return;
      }
      const data = await fetchSecure(accessToken, `${appConfig.apiUrl}uploads/${uploadId}/rerun`, {
        method: "POST",
        data: inputIds ? { inputIds } : {},
      });
      if (!data || data.status !== "OK") {
        setError(data?.message || "The test set could not be rerun.");
        return;
      }

      setNotice(`${data.data.total} input(s) queued again.`);
      setShowModalInputs(false);
      if (pageNumber === 1) {
        await getUploads({ silent: true });
      } else {
        setPageNumber(1);
      }
    } finally {
      setRerunning((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleInput = (inputId) => {
    setExpandedInputs((prevState) => ({
      ...prevState,
      [inputId]: !prevState[inputId],
    }));
  };

  return (
    <>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modal-stacked" backdropClassName="modal-stacked-backdrop">
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {compiledMode ? `Add comments to compiled drawing (${selectedDrawingIds.length})` : "Add comments to drawing"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label upload-label" htmlFor="drawing-comments">Comments (optional)</label>
          <textarea
            id="drawing-comments"
            className="form-control"
            rows="4"
            placeholder="Enter your comments here..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          ></textarea>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowModal(false);
              if (compiledMode) {
                downloadCompiledAEDrawings(selectedDrawingIds, comments);
              } else {
                downloadAEDrawing(currentDownloadData.id, setAEDrawingFileName(currentDownloadData.name), comments);
              }
            }}
          >
            Download
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!confirmRerun} onHide={() => setConfirmRerun(null)} centered className="modal-stacked" backdropClassName="modal-stacked-backdrop">
        <Modal.Header closeButton>
          <Modal.Title as="h5">Run again?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmRerun && `Run ${confirmRerun.label} again? A new test set is created; the current results are kept.`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmRerun(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const { uploadId, inputIds } = confirmRerun;
              askNotificationPermission();
              setConfirmRerun(null);
              rerunUpload(uploadId, inputIds);
            }}
          >
            Run again
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModalInputs} onHide={handleCloseInputs} size="xl" dialogClassName="modal-inputs" scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{selectedName} inputs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center gap-2 mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={downloadStatus[`set-${selectedUploadId}`] === "Downloading..." || !inputs.some((input) => input.outputs.some((o) => !isTraceOutput(o)))}
              title="Download every BOM and comparison of this test set in one .zip"
              onClick={() => downloadAllFiles(selectedUploadId, selectedName)}
            >
              Download all files
            </button>
            <DownloadStatus text={downloadStatus[`set-${selectedUploadId}`]} />
            {SHOW_COMPILED_PDF && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  disabled={selectedDrawingIds.length === 0 || downloadingCompiled}
                  onClick={() => {
                    setCompiledMode(true);
                    setComments("");
                    setShowModal(true);
                  }}
                >
                  Export Selected as One PDF ({selectedDrawingIds.length})
                </button>
                {selectedDrawingIds.length > 0 && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearDrawingSelection}>
                    Clear
                  </button>
                )}
                <DownloadStatus text={compiledStatus} />
              </>
            )}
          </div>
          <div className="table-responsive">
            <table className="table inputs-table align-middle">
              <thead>
                <tr>
                  <th><span className="visually-hidden">Details</span></th>
                  <th title="Run Id, used as Previous Run Id to compare against this run">Test / Run</th>
                  <th>ESOI</th>
                  <th>Run</th>
                  <th>Status</th>
                  <th title="Counts from the Summary sheet of the comparison">
                    Result
                    <FieldHelp label="Result" section="results">
                      What the comparison found, read from its Summary sheet: missing items, changed values and quantities.
                      Red means a part is NOT FOUND or ERROR. For Infor runs, click a count to open those rows in the
                      comparison.
                    </FieldHelp>
                  </th>
                  <th><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {inputs.map((input) => {
                  const comparisons = input.outputs.filter(isComparisonOutput);
                  const summaryOutput = summaryOutputFor(input);
                  const summary = summaryOutput ? summaries[summaryOutput.id] : null;
                  const expanded = !!expandedInputs[input.id];
                  return (
                    <Fragment key={input.id}>
                      <tr className="input">
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-link inputs-table__toggle"
                            onClick={() => toggleInput(input.id)}
                            aria-expanded={expanded}
                            aria-controls={`input-details-${input.id}`}
                            aria-label={`${expanded ? "Hide" : "Show"} details and files of ${input.test}`}
                          >
                            <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
                          </button>
                        </td>
                        <td>
                          {input.test}
                          <div className="text-muted small">#{input.id}</div>
                        </td>
                        <td>
                          {input.configurationId}
                          {input.formatId && <div className="text-muted small">Format {input.formatId}</div>}
                        </td>
                        <td className="inputs-table__run">{getRunLabel(input)}</td>
                        <td>
                          {input.status}
                          {input.status === "Failed" && input.errorMessage && (
                            <div className="text-danger small mt-1 inputs-table__error" title={input.errorMessage}>
                              {input.errorMessage}
                            </div>
                          )}
                        </td>
                        <td className="inputs-table__result">
                          <ComparisonSummary summary={summary} linkFor={(item) => chipUrl(input, summary.data, item)} />
                        </td>
                        <td className="inputs-table__actions">
                          <div className="inputs-actions">
                          <ViewBomButton outputs={input.outputs.filter(isBomOutput).sort((a, b) => a.id - b.id)} />
                          <CompareButton input={input} />
                          <ExcelButton
                            comparisons={comparisons}
                            primary={!canOpenComparison(input)}
                            busy={(output) => downloadStatus[`file-${output.id}`] === "Downloading..."}
                            onDownload={(output) => downloadOutputFile(output.id, output.name)}
                          />
                          {comparisons.map((output) => (
                            <DownloadStatus key={output.id} text={downloadStatus[`file-${output.id}`]} />
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={!FINISHED_STATUSES.includes(input.status) || rerunning[`input-${input.id}`]}
                            title={FINISHED_STATUSES.includes(input.status) ? "Run this input again as a new test set" : "Available when the input finishes"}
                            onClick={() => setConfirmRerun({ uploadId: input.uploadId, inputIds: [input.id], label: `input ${input.test} (${input.configurationId})` })}
                          >
                            {rerunning[`input-${input.id}`] ? "Queuing..." : "Rerun"}
                          </button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr id={`input-details-${input.id}`}>
                          <td colSpan={7}>
                            <dl className="inputs-details">
                              <dt>CodeString</dt>
                              <dd className="inputs-details__code">{input.codeString || "-"}</dd>
                              <dt>Post Config Queue</dt>
                              <dd>{input.enablePostConfigQueue || "-"}</dd>
                              <dt>Priority</dt>
                              <dd>{input.priority ?? "-"}</dd>
                              {summary?.data?.details?.length > 0 && (
                                <>
                                  <dt>Summary</dt>
                                  <dd>
                                    <ul className="inputs-details__summary">
                                      {summary.data.details.map((line) => {
                                        // A tab with differences opens on that tab of the comparison
                                        const tab = canOpenComparison(input) ? parseSummaryLine(line)?.tab : null;
                                        return (
                                          <li key={line}>
                                            {tab ? (
                                              <a href={compareUrl(input, { tab })} target="_blank" rel="noopener noreferrer" title={`Open ${tab} in the comparison`}>
                                                {line}
                                              </a>
                                            ) : (
                                              line
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </dd>
                                </>
                              )}
                            </dl>
                            <div className="card">
                              <div className="card-header py-2 small fw-semibold">Files</div>
                              <ul className="list-group list-group-flush">
                                {input.outputs.length === 0 && (
                                  <li className="list-group-item text-muted small">
                                    {input.status === "Failed" ? "No files, the input failed." : "No files yet."}
                                  </li>
                                )}
                                {[...input.outputs]
                                  .sort((a, b) => Number(isComparisonOutput(b)) - Number(isComparisonOutput(a)))
                                  .map((output) => {
                                    const kind = getOutputKind(output);
                                    const isRaw = !isComparisonOutput(output) && !isTraceOutput(output) && !isExternalOutput(output);
                                    return (
                                      <li key={`output-${output.id}`} className="list-group-item d-flex align-items-center flex-wrap gap-2">
                                        {SHOW_COMPILED_PDF && isRaw && (
                                          <input
                                            type="checkbox"
                                            className="form-check-input m-0"
                                            aria-label={`Select ${output.name} for the compiled PDF`}
                                            checked={selectedDrawingIds.includes(output.id)}
                                            onChange={() => toggleDrawingSelection(output.id)}
                                          />
                                        )}
                                        <span className={`badge ${kind.badge}`}>{kind.label}</span>
                                        {output.environment && (
                                          <span className="badge text-bg-light border">{environmentLabel(output.environment)}</span>
                                        )}
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 text-start inputs-file"
                                          title="Download Excel"
                                          onClick={() => downloadOutputFile(output.id, output.name)}
                                        >
                                          {output.name}
                                        </button>
                                        {isBomOutput(output) && (
                                          <a className="small" href={bomViewerUrl(output)} target="_blank" rel="noopener noreferrer">
                                            View
                                          </a>
                                        )}
                                        <DownloadStatus text={downloadStatus[`file-${output.id}`]} />
                                        {isRaw && (
                                          <span className="ms-md-auto d-flex flex-wrap align-items-center gap-2">
                                            <span>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={downloadingIds[output.id]}
                                                onClick={() => downloadOutputFolders(output.id, setFolderZipName(output.name))}
                                              >
                                                Download Parts Zip
                                              </button>
                                              <DownloadStatus text={downloadStatus[output.id]} />
                                            </span>
                                            <span>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={downloadingIds[output.id]}
                                                onClick={() => {
                                                  setCompiledMode(false);
                                                  setCurrentDownloadData({ id: output.id, name: output.name });
                                                  setComments("");
                                                  setShowModal(true);
                                                }}
                                              >
                                                Download AE Drawing
                                              </button>
                                              <DownloadStatus text={downloadStatusDrawing[output.id]} />
                                            </span>
                                          </span>
                                        )}
                                      </li>
                                    );
                                  })}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseInputs}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="uploads-panel card border-0 shadow-sm mb-4" data-tour="test-sets">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
            <div>
              <h3 className="upload-panel__title mb-1">Test sets</h3>
              <p className="upload-panel__subtitle mb-0">Open a test set to see its inputs, errors and output files.</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              {hasActiveUploads && (
                <span className="uploads-live" title={`Refreshing every ${REFRESH_INTERVAL_MS / 1000}s while inputs are running`}>
                  <span className="uploads-live__dot" /> Live
                </span>
              )}
              <button type="button" className="btn btn-sm btn-outline-secondary px-3" onClick={() => getUploads({ silent: true })}>
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger upload-alert d-flex justify-content-between align-items-center" role="alert">
              <span>{error}</span>
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}
          {notice && (
            <div className="alert alert-success upload-alert d-flex justify-content-between align-items-center" role="status">
              <span>{notice}</span>
              <button type="button" className="btn-close" onClick={() => setNotice(null)}></button>
            </div>
          )}
          {finishedNotices.finished.map((item) => (
            <div
              key={item.id}
              className={`alert ${item.failed ? "alert-warning" : "alert-success"} upload-alert d-flex justify-content-between align-items-center gap-2`}
              role="status"
            >
              <span>
                <b>{item.name}</b> finished: {item.text}.
              </span>
              <span className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => {
                    finishedNotices.dismiss(item.id);
                    fetchInputs(item.id, item.name);
                  }}
                >
                  Open
                </button>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => finishedNotices.dismiss(item.id)}></button>
              </span>
            </div>
          ))}

          {SHOW_TEST_SET_FILTER && (filter || uploads.filter(Boolean).length > 0) && (
            <div className="d-flex align-items-center gap-2 mb-2">
              <input
                type="search"
                className="form-control form-control-sm uploads-filter"
                style={{ maxWidth: 320 }}
                placeholder="Filter by test set, CodeString or ESOI"
                aria-label="Filter test sets by test set, CodeString or ESOI"
                value={filter}
                onChange={handleFilterChange}
              />
              {filter && (
                <>
                  <button type="button" className="btn btn-sm btn-link text-nowrap" onClick={() => handleFilterChange({ target: { value: "" } })}>
                    Clear
                  </button>
                  <span className="text-muted small text-nowrap">
                    {totalItems} {totalItems === 1 ? "match" : "matches"}
                  </span>
                </>
              )}
            </div>
          )}

          {uploads.filter(Boolean).length === 0 && filter ? (
            <div className="uploads-empty">
              <div className="fw-semibold">No test sets match "{filter.trim()}"</div>
              <div className="small">Try a different test set name, CodeString or ESOI.</div>
            </div>
          ) : uploads.filter(Boolean).length === 0 ? (
            <div className="uploads-empty">
              <div className="fw-semibold">No test sets yet</div>
              <ol className="uploads-empty__steps small">
                <li>Import your Excel above, or fill a row with the ESOI, Format Number and CodeString.</li>
                <li>Choose BOM from and Compare with.</li>
                <li>Click Create test set. It runs in the background and shows up here.</li>
              </ol>
              <div className="small">
                <Link to="/?tour=1">Take the tour</Link> or read the <a href="/help" target="_blank" rel="noopener noreferrer">guide</a>.
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table uploads-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Test set</th>
                    <th>Created</th>
                    <th className="uploads-table__progress">Progress</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.filter(Boolean).map((upload) => {
                    const status = getUploadStatus(upload);
                    const display = getUploadDisplay(upload);
                    return (
                      <tr key={upload.id} className="uploads-table__row" onClick={() => fetchInputs(upload.id, display.text)}>
                        <td>
                          <UploadName upload={upload} display={display} onSave={(title) => saveUploadName(upload.id, title)} />
                        </td>
                        <td className="text-nowrap text-muted">{formatCreated(upload.created)}</td>
                        <td className="uploads-table__progress">
                          <UploadProgress upload={upload} />
                        </td>
                        <td>
                          <span className={`uploads-status uploads-status--${status.tone}`}>{status.label}</span>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchInputs(upload.id, display.text);
                            }}
                          >
                            View inputs
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary text-nowrap ms-2"
                            disabled={isUploadActive(upload) || !upload.total || rerunning[`upload-${upload.id}`]}
                            title={isUploadActive(upload) ? "Available when all inputs finish" : "Run all inputs again as a new test set"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmRerun({ uploadId: upload.id, inputIds: null, label: `the ${upload.total} input(s) of "${display.text}"` });
                            }}
                          >
                            {rerunning[`upload-${upload.id}`] ? "Queuing..." : "Rerun"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {(totalPages > 1 || pageSize !== 10 || uploads.filter(Boolean).length > 0) && (
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                <span className="text-muted small">Page {pageNumber} of {Math.max(totalPages, 1)}</span>
                <label className="d-flex align-items-center gap-2 text-muted small mb-0">
                  Rows per page
                  <select className="form-select form-select-sm w-auto" value={pageSize} onChange={handlePageSizeChange}>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="btn-group">
                <button className="btn btn-sm btn-outline-secondary" onClick={handlePreviousPage} disabled={pageNumber === 1}>
                  Previous
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={handleNextPage} disabled={pageNumber >= totalPages}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
